import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Zap, Fingerprint } from "lucide-react";
import { isPasskeySupported, signInWithPasskey } from "../pwa/passkeyApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REMEMBERED_EMAIL_KEY = "fuelled.portal.remembered_email";

export default function AffiliateLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [showPasskey, setShowPasskey] = useState(false);
  const nav = useNavigate();

  // Prefill the last-used email so the user only types their password.
  useEffect(() => {
    try {
      const saved = localStorage.getItem(REMEMBERED_EMAIL_KEY);
      if (saved) setEmail(saved);
    } catch {}
    // Show the passkey button only when the browser exposes the WebAuthn API.
    setShowPasskey(isPasskeySupported());
  }, []);

  const doPasskeyLogin = async () => {
    setPasskeyBusy(true);
    try {
      const data = await signInWithPasskey();
      toast.success(`Welcome back, ${data.affiliate.name}`);
      nav("/affiliate");
    } catch (e) {
      // Distinguish "user dismissed the prompt" from "no credential on this device"
      const name = e?.name || "";
      const detail = e?.response?.data?.detail || "";
      if (name === "NotAllowedError") {
        toast.message("Passkey prompt cancelled");
      } else if (detail === "Unknown passkey" || name === "InvalidStateError") {
        toast.info("No passkey found on this device. Sign in with your password, then add one from Settings.");
      } else {
        toast.error(detail || "Passkey sign-in failed. Try your password instead.");
      }
    } finally {
      setPasskeyBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/affiliate/auth/login`,
        { email, password, remember },
        { withCredentials: true }
      );
      // Remember (or forget) the email locally for the next visit.
      try {
        if (remember) localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
        else localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      } catch {}
      // Ask the browser's credential manager (Chrome/Edge/Android) to offer
      // saving the password — iOS Safari uses the native keychain via the
      // form's autocomplete attributes below. Chrome sometimes rejects
      // asynchronously with "public key credentials not supported"; that
      // shouldn't crash the login, so swallow it.
      try {
        if (window.PasswordCredential) {
          const cred = new window.PasswordCredential({
            id: email,
            password,
            name: data.affiliate?.name || email,
          });
          const p = navigator.credentials?.store?.(cred);
          if (p && typeof p.catch === "function") p.catch(() => {});
        }
      } catch {}
      toast.success(`Welcome, ${data.affiliate.name}`);
      if (data.must_change_password) {
        toast.info("Please change your password from your dashboard soon.");
      }
      nav("/affiliate");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-sm space-y-6">
        <div data-testid="affiliate-login-welcome" className="text-center space-y-2">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight leading-tight">
            Welcome to the <span className="text-orange-400">Fuelled Coach App</span>
          </h1>
          <p className="text-sm text-neutral-400 leading-relaxed">
            We're glad you're here. Let's help people win in fitness.
          </p>
        </div>
        <form
          onSubmit={submit}
          data-testid="affiliate-login-form"
          method="post"
          action={`${API}/affiliate/auth/login`}
          autoComplete="on"
          className="rounded-3xl border border-neutral-800 bg-neutral-900/80 backdrop-blur-xl p-8 space-y-5 shadow-2xl"
        >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-orange-500 flex items-center justify-center">
            <Zap className="h-5 w-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-lg font-bold tracking-tight">Fuelled Portal</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Affiliate Access</div>
          </div>
        </div>

        {showPasskey && (
          <>
            <button
              type="button"
              onClick={doPasskeyLogin}
              disabled={passkeyBusy || busy}
              data-testid="affiliate-login-passkey"
              className="w-full h-11 rounded-xl border border-orange-500/40 bg-orange-500/10 hover:bg-orange-500/20 text-orange-200 text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Fingerprint className="h-4 w-4" />
              {passkeyBusy ? "Waiting for Face ID…" : "Sign in with Face ID / fingerprint"}
            </button>
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-neutral-600">
              <div className="flex-1 border-t border-neutral-800" />
              <span>or use password</span>
              <div className="flex-1 border-t border-neutral-800" />
            </div>
          </>
        )}

        <div className="space-y-3 pt-2">
          <div>
            <label htmlFor="affiliate-email" className="text-[10px] uppercase tracking-widest text-neutral-500">Email</label>
            <input
              id="affiliate-email"
              name="email"
              data-testid="affiliate-login-email"
              type="email"
              required
              autoComplete="username webauthn"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@fuelled.in"
              className="mt-1 w-full h-11 rounded-xl bg-neutral-800 border border-neutral-700 px-4 text-sm outline-none focus:border-orange-500"
            />
          </div>
          <div>
            <label htmlFor="affiliate-password" className="text-[10px] uppercase tracking-widest text-neutral-500">Password</label>
            <input
              id="affiliate-password"
              name="password"
              data-testid="affiliate-login-password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full h-11 rounded-xl bg-neutral-800 border border-neutral-700 px-4 text-sm outline-none focus:border-orange-500"
            />
          </div>
          <label className="flex items-center gap-2 pt-1 select-none cursor-pointer">
            <input
              data-testid="affiliate-login-remember"
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-4 w-4 rounded border-neutral-600 bg-neutral-800 text-orange-500 focus:ring-orange-500 accent-orange-500"
            />
            <span className="text-xs text-neutral-400">
              Keep me signed in on this device <span className="text-neutral-600">· 90 days</span>
            </span>
          </label>
        </div>
        <button
          data-testid="affiliate-login-submit"
          disabled={busy || passkeyBusy}
          className="w-full h-11 rounded-xl bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white text-sm font-semibold transition-colors"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <div className="text-[11px] text-neutral-500 pt-2 text-center leading-relaxed">
          Access issued by your Fuelled admin. Ask them if you don&apos;t have credentials yet.
          <br />
          <span className="text-neutral-600">Your browser will offer to save your password after sign in.</span>
        </div>
      </form>
      </div>
    </div>
  );
}
